import React, { useCallback } from 'react';
import { Avatar, Box, Button, Chip, Snackbar, Stack, Tooltip, Typography } from '@mui/material';
import ShareOutlinedIcon from '@mui/icons-material/ShareOutlined';
import VerifiedIcon from '@mui/icons-material/Verified';
import RoomOutlinedIcon from '@mui/icons-material/RoomOutlined';
import BoltOutlinedIcon from '@mui/icons-material/BoltOutlined';
import type { PublicSupplierProfileVO } from '../../../types/model/valueObject/account/PublicSupplierProfileVO';

interface SupplierProfileHeaderProps {
  profile: PublicSupplierProfileVO;
}

const getInitials = (name: string): string => {
  return name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map(w => w[0].toUpperCase())
    .join('');
};

const getUniqueStateCount = (locations: PublicSupplierProfileVO['locations']): number => {
  return new Set(locations.map(l => l.state).filter(Boolean)).size;
};

const SupplierProfileHeader = ({ profile }: SupplierProfileHeaderProps) => {
  const { companyName, companyDescription, logoUrl, locations, yearFounded, profileUrl, verified } = profile;
  const hasInstantApproval = locations.some(l => l.hasInstantApproval);
  const [snackOpen, setSnackOpen] = React.useState(false);
  const stateCount = getUniqueStateCount(locations);

  const handleShare = useCallback(() => {
    const url = profileUrl
      ? `${window.location.origin}${profileUrl}`
      : window.location.href;
    const copyFallback = () => {
      const ta = document.createElement('textarea');
      ta.value = url;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      setSnackOpen(true);
    };
    if (navigator.clipboard) {
      navigator.clipboard.writeText(url).then(() => setSnackOpen(true)).catch(copyFallback);
    } else {
      copyFallback();
    }
  }, [profileUrl]);

  return (
    <>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={{ xs: 2, sm: 3 }}
        alignItems={{ xs: 'flex-start', sm: 'flex-start' }}
        sx={{ py: 4 }}
      >
        {/* Avatar */}
        <Avatar
          src={logoUrl || undefined}
          alt={companyName}
          sx={{
            width: { xs: 72, sm: 88 },
            height: { xs: 72, sm: 88 },
            fontSize: '2rem',
            bgcolor: 'primary.main',
            flexShrink: 0,
          }}
        >
          {getInitials(companyName)}
        </Avatar>

        {/* Content */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction="row" alignItems="center" sx={{ mb: 0, width: '100%' }} justifyContent="space-between">
            <Stack direction="row" alignItems="center" spacing={0.75}>
              <Typography
                variant="h1"
                sx={{
                  fontFamily: 'Satoshi-Variable, sans-serif',
                  fontWeight: 700,
                  fontSize: { xs: '1.5rem', sm: '1.75rem' },
                  lineHeight: 1.3,
                }}
              >
                {companyName}
              </Typography>

              {verified && (
                <Tooltip
                  title="Verified supplier — SecurSpace has confirmed this business's identity and insurance."
                  arrow
                  placement="top"
                >
                  <VerifiedIcon
                    aria-label="Verified supplier"
                    sx={{
                      color: 'primary.main',
                      fontSize: '1.5rem',
                      position: 'relative',
                      top: 1,
                      flexShrink: 0,
                    }}
                  />
                </Tooltip>
              )}
            </Stack>
            <Button
              variant="outlined"
              size="small"
              startIcon={<ShareOutlinedIcon />}
              onClick={handleShare}
              sx={{
                borderColor: '#E9E9FB',
                color: 'text.primary',
                fontFamily: 'Inter, sans-serif',
                textTransform: 'none',
                flexShrink: 0,
                '&:hover': { borderColor: 'primary.main', bgcolor: 'rgba(63,81,181,0.04)' },
              }}
            >
              Share
            </Button>
          </Stack>

          {/* Meta chips row */}
          <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: 'wrap', gap: 0.75 }}>
            {locations.length > 0 && (
              <Chip
                icon={<RoomOutlinedIcon sx={{ fontSize: '0.9rem !important' }} />}
                label={`${locations.length} location${locations.length !== 1 ? 's' : ''}`}
                size="small"
                variant="outlined"
                sx={{ borderColor: '#E9E9FB', backgroundColor: '#F5F5FB', fontFamily: 'Inter, sans-serif', fontSize: '0.8rem' }}
              />
            )}
            {stateCount > 0 && (
              <Chip
                label={`${stateCount} state${stateCount !== 1 ? 's' : ''}`}
                size="small"
                variant="outlined"
                sx={{ borderColor: '#E9E9FB', backgroundColor: '#F5F5FB', fontFamily: 'Inter, sans-serif', fontSize: '0.8rem' }}
              />
            )}
            {yearFounded && (
              <Chip
                label={`Est. ${yearFounded}`}
                size="small"
                variant="outlined"
                sx={{ borderColor: '#E9E9FB', backgroundColor: '#F5F5FB', fontFamily: 'Inter, sans-serif', fontSize: '0.8rem' }}
              />
            )}
            {hasInstantApproval && (
              <Chip
                icon={<BoltOutlinedIcon sx={{ fontSize: '0.9rem !important' }} />}
                label="Instant approval"
                size="small"
                variant="outlined"
                sx={{ borderColor: '#E9E9FB', backgroundColor: '#F5F5FB', fontFamily: 'Inter, sans-serif', fontSize: '0.8rem' }}
              />
            )}
          </Stack>

          {/* Description */}
          {companyDescription && (
            <Typography
              sx={{
                fontFamily: 'Inter, sans-serif',
                fontSize: '0.95rem',
                lineHeight: 1.6,
                color: 'text.secondary',
                mt: 1.5,
                maxWidth: 680,
              }}
            >
              {companyDescription}
            </Typography>
          )}


        </Box>
      </Stack>

      <Snackbar
        open={snackOpen}
        autoHideDuration={2500}
        onClose={() => setSnackOpen(false)}
        message="Link copied to clipboard"
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </>
  );
};

export default SupplierProfileHeader;

